import { isDefined } from '@sgme/fp';
import clsx from 'clsx';

import { SgFormattedMessage } from '../../SgFormattedMessage';
import { useSgPickerInputContext } from './context';
import { DropdownItemButton } from './DropdownItemButton';
import type { SgPickerItem } from './types';

type SgPickerMenuProps = {
  'data-e2e'?: string;
};

export function SgPickerMenu(props: SgPickerMenuProps) {
  const { 'data-e2e': dataE2E } = props;

  const {
    color,
    size,
    createCustomItemButtonLabel,
    inputValue,
    showNoResultLabel,
    showTypingHintLabel,
    isOpen,
    items = [],
    itemSelection = 'single',
    selectedItems = [],
    selectedItem,
    highlightedIndex,
    getMenuProps,
    getItemProps,
    refs,
    floatingStyles,
    maxSelectionState = 'normal',
    maxSelectionMessage = 'Limite de sélection atteinte',
    hasTooManyResults = false,
    dropdownRef,
  } = useSgPickerInputContext();

  return (
    <div
      {...getMenuProps(
        {
          /**
           * Dropdown menu references management
           *
           * This callback function handles two references simultaneously:
           *
           * 1. refs.setFloating(node) - Reference for Floating UI
           *    Used by the Floating UI library to calculate and maintain
           *    the dropdown position relative to the reference element (input).
           *    Essential for automatic positioning and repositioning.
           *
           * 2. dropdownRef.current - Publicly exposed reference
           *    Allows component consumers to directly access the dropdown DOM
           *    for custom operations (scroll, focus, measurements, etc.).
           *    The condition prevents unnecessary reassignments if the reference hasn't changed.
           *
           * The ref is passed inside getMenuProps (not after the spread) so that downshift
           * merges it with its own internal menuRef. Placing the ref after the spread would
           * override downshift's ref, causing the "forgot to call getMenuProps" warning.
           */
          ref: (node: HTMLDivElement | null) => {
            refs.setFloating(node);
            if (dropdownRef && dropdownRef.current !== node) {
              dropdownRef.current = node;
            }
          },
        },
        { suppressRefError: true },
      )}
      data-e2e={isDefined(dataE2E) ? `${dataE2E}-menu` : undefined}
      className={clsx('sg-picker-dropdown-menu dropdown-menu bg-lvl1 p-0 z-10 w-100', { show: isOpen })}
      style={{
        ...floatingStyles,
        overflowY: 'auto',
      }}
    >
      {isOpen && items?.length === 0 && !inputValue && showTypingHintLabel && (
        <button type="button" className={`btn btn-${size} text-muted w-100 border-0 text-start`} disabled>
          <SgFormattedMessage id="typing" />
        </button>
      )}

      {isOpen && maxSelectionState === 'error' && (
        <button
          type="button"
          className={`list-group-item text-warning fst-italic btn btn-${size} w-100 border-0 text-start`}
          disabled
        >
          <i className="bi bi-exclamation-triangle me-2" />
          {maxSelectionMessage}
        </button>
      )}

      {/* Ancien bouton Select All/Deselect All - maintenant intégré dans la navigation */}

      {isOpen &&
        items.map((item, idx) => {
          // Check if this is the select all button
          const extendedItem = item as SgPickerItem & { isCreateCustomItem?: boolean; isSelectAllButton?: boolean };

          if (extendedItem.isSelectAllButton) {
            const areAllFilteredItemsSelected = () => {
              const selectableItems = items.filter((item) => !item.disabled && !(item as any).isSelectAllButton);
              return (
                selectableItems.length > 0 &&
                selectableItems.every((item) => selectedItems.some((selected) => selected.value === item.value))
              );
            };

            return (
              <DropdownItemButton
                key={item.value}
                item={item}
                index={idx}
                isHighlighted={Boolean(highlightedIndex === idx)}
                getItemProps={getItemProps}
                color={color}
                size={size}
                itemType="selectAll"
                areAllSelected={areAllFilteredItemsSelected()}
              />
            );
          }

          // Check if this is the custom item creation entry
          if (extendedItem.isCreateCustomItem) {
            return (
              <DropdownItemButton
                key={item.value}
                item={item}
                index={idx}
                isHighlighted={Boolean(highlightedIndex === idx)}
                getItemProps={getItemProps}
                color={color}
                size={size}
                itemType="createCustom"
                inputValue={inputValue}
                createCustomItemButtonLabel={createCustomItemButtonLabel}
              />
            );
          }

          // Regular item
          return (
            <DropdownItemButton
              key={item.value}
              item={item}
              index={idx}
              isHighlighted={Boolean(highlightedIndex === idx)}
              getItemProps={getItemProps}
              color={color}
              size={size}
              itemType="regular"
              selectedItems={selectedItems}
              selectedItem={selectedItem}
              itemSelection={itemSelection}
            />
          );
        })}

      {isOpen && items?.length === 0 && inputValue && showNoResultLabel && (
        <button type="button" className={`btn btn-${size} text-muted w-100 border-0 text-start`} disabled>
          <SgFormattedMessage id="notFound" />
        </button>
      )}

      {isOpen && hasTooManyResults && (
        <button type="button" className={`btn btn-${size} text-muted w-100 border-0 text-start`} disabled>
          <SgFormattedMessage id="picker.tooManyResults" />
        </button>
      )}
    </div>
  );
}
